import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DashboardWrapper from "../../components/DashboardWrapper";
import userManageService from "../../services/userManageService";
import productService from "../../services/productService";
import Breadcrumb from "../../components/Breadcrumbdashboard";
import authService from "../../services/authService";

export default function CreateCustomer() {
  const navigate = useNavigate();

  // ============================================================
  // FORM DATA
  // ============================================================

  const [formData, setFormData] = useState({
    username: "",
    role_id: "",
    phone_number: "",
    email: "",
    password: "",
    password_confirmation: "",
  });

  // ============================================================
  // STATES
  // ============================================================

  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ============================================================
  // LOGGED-IN USER ROLE
  // ============================================================

  const currentUser = authService.getCurrentUser();

  const getLoggedInRole = (user) => {
    if (!user) return "";

    let roleValue =
      user.role ||
      user.role_name ||
      user.roleName ||
      user.user_role ||
      user.userRole ||
      user.roles ||
      user.userRoles ||
      user.data?.role ||
      user.data?.role_name ||
      user.data?.roleName ||
      user.data?.user?.role ||
      user.data?.user?.role_name;

    // If role is array
    if (Array.isArray(roleValue)) {
      roleValue = roleValue[0];
    }

    // If role is object
    if (typeof roleValue === "object" && roleValue !== null) {
      roleValue =
        roleValue.name ||
        roleValue.role ||
        roleValue.role_name ||
        roleValue.roleName ||
        roleValue.slug ||
        roleValue.key ||
        roleValue.code;
    }

    return String(roleValue || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]/g, "");
  };

  const loggedInRole = getLoggedInRole(currentUser);

  const isSuperAdmin =
    loggedInRole === "superadmin" ||
    loggedInRole === "superadministrator";

  const isTelecaller = loggedInRole === "telecaller";

  // ============================================================
  // FETCH ROLES
  // ============================================================

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setRolesLoading(true);

    try {
      const response = await productService.getAllRoles();

      console.log("Roles API Response:", response);

      const roleData = response?.data?.data || response?.data || [];

      if (Array.isArray(roleData)) {
        // ==========================================================
        // FILTER ROLES BASED ON LOGGED-IN USER
        // ==========================================================

        let filteredRoles = roleData;

        // ----------------------------------------------------------
        // SUPER ADMIN
        // Hide CUSTOMER role
        // ----------------------------------------------------------

        if (isSuperAdmin) {
          filteredRoles = roleData.filter((role) => {
            const roleName = String(
              role?.name ||
                role?.role ||
                role?.role_name ||
                role?.roleName ||
                role?.slug ||
                ""
            )
              .trim()
              .toLowerCase()
              .replace(/[\s_-]/g, "");

            return roleName !== "customer";
          });
        }

        // ----------------------------------------------------------
        // TELECALLER
        // Show ONLY CUSTOMER role
        // ----------------------------------------------------------

        if (isTelecaller) {
          filteredRoles = roleData.filter((role) => {
            const roleName = String(
              role?.name ||
                role?.role ||
                role?.role_name ||
                role?.roleName ||
                role?.slug ||
                ""
            )
              .trim()
              .toLowerCase()
              .replace(/[\s_-]/g, "");

            return roleName === "customer";
          });
        }

        console.log("Logged In Role:", loggedInRole);
        console.log("All Roles:", roleData);
        console.log("Filtered Roles:", filteredRoles);

        setRoles(filteredRoles);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Failed to fetch roles:", error);

      setRoles([]);

      Swal.fire({
        icon: "error",
        title: "Failed to Load Roles",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch roles. Please try again.",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    } finally {
      setRolesLoading(false);
    }
  };

  // ============================================================
  // HANDLE INPUT CHANGE
  // ============================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    // ==========================================================
    // ROLE CHANGE
    // ==========================================================

    if (name === "role_id") {
      const selected = roles.find(
        (role) => String(role.id) === String(value)
      );

      setFormData((prev) => ({
        ...prev,
        role_id: value,
      }));

      setSelectedRole(selected || null);

      return;
    }

    // ==========================================================
    // PHONE NUMBER
    // ==========================================================

    if (name === "phone_number") {
      // Allow only numbers
      if (!/^\d*$/.test(value)) {
        return;
      }

      // Maximum 10 digits
      if (value.length > 10) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        phone_number: value,
      }));

      return;
    }

    // ==========================================================
    // EMAIL
    // ==========================================================

    if (name === "email") {
      setFormData((prev) => ({
        ...prev,
        email: value,
      }));

      return;
    }

    // ==========================================================
    // OTHER FIELDS
    // ==========================================================

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // HANDLE SUBMIT
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ==========================================================
    // ROLE VALIDATION
    // ==========================================================

    if (!formData.role_id) {
      Swal.fire({
        icon: "warning",
        title: "Role Required",
        text: "Please select a role.",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });

      return;
    }

    // ==========================================================
    // PHONE VALIDATION
    // ==========================================================

    if (!/^\d{10}$/.test(formData.phone_number)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Phone Number",
        text: "Phone number must be exactly 10 digits.",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });

      return;
    }

    // ==========================================================
    // EMAIL VALIDATION
    // ==========================================================

    const emailRegex =
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Email",
        text: "Please enter a valid email address.",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });

      return;
    }

    // ==========================================================
    // PASSWORD VALIDATION
    // ==========================================================

    if (formData.password !== formData.password_confirmation) {
      Swal.fire({
        icon: "error",
        title: "Password Mismatch",
        text: "Password and Confirm Password must match.",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });

      return;
    }

    // ==========================================================
    // START LOADING
    // ==========================================================

    setLoading(true);

    try {
      // ========================================================
      // CREATE CUSTOMER
      // ========================================================

      console.log("Create Customer Payload:", formData);

      await userManageService.createCustomer(formData);

      // ========================================================
      // SUCCESS
      // ========================================================

      await Swal.fire({
        icon: "success",
        title: "Customer Created",
        text: "The customer has been created successfully.",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });

      // ========================================================
      // RESET FORM
      // ========================================================

      setFormData({
        username: "",
        role_id: "",
        phone_number: "",
        email: "",
        password: "",
        password_confirmation: "",
      });

      setSelectedRole(null);

      // ========================================================
      // NAVIGATE
      // ========================================================

      navigate("/admin/customer-management");
    } catch (err) {
      console.error("Create customer error:", err);

      const errors =
        err.response?.data?.errors ||
        err.response?.data?.message ||
        err.message ||
        "Failed to create user";

      Swal.fire({
        icon: "error",
        title: "Error",
        html:
          typeof errors === "object"
            ? Object.values(errors)
                .flat()
                .map((e) => `<div>${e}</div>`)
                .join("")
            : errors,
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // JSX
  // ============================================================

  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4">

        {/* ======================================================
            BREADCRUMB
        ====================================================== */}

        <div className="d-flex justify-content-between align-items-center mb-4">
          <Breadcrumb />
        </div>

        {/* ======================================================
            CARD
        ====================================================== */}

        <div className="card shadow">

          <div className="card-body">

            <form onSubmit={handleSubmit}>

              <div className="row g-3">

                {/* ==================================================
                    ROLE DESCRIPTION
                ================================================== */}

                {selectedRole && (
                  <div className="col-12">
                    <div
                      className="d-flex align-items-center"
                      style={{
                        padding: "7px 12px",
                        backgroundColor: "#f8f9fa",
                        border: "1px solid #e9ecef",
                        borderLeft: "3px solid #FB6C22",
                        borderRadius: "5px",
                      }}
                    >
                      <i
                        className="bx bx-info-circle me-2"
                        style={{
                          fontSize: "18px",
                          color: "#1b8e45",
                        }}
                      />

                      <div
                        style={{
                          fontSize: "14px",
                          color: "#6c757d",
                          lineHeight: "1.4",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#343a40",
                            marginRight: "5px",
                          }}
                        >
                          {selectedRole.name}:
                        </span>

                        {selectedRole.description}
                      </div>
                    </div>
                  </div>
                )}

                {/* ==================================================
                    ROLE
                ================================================== */}

                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold">
                    Role
                  </label>

                  <select
                    name="role_id"
                    className="form-select"
                    value={formData.role_id}
                    onChange={handleChange}
                    required
                    disabled={rolesLoading}
                  >
                    <option value="">
                      {rolesLoading
                        ? "Loading roles..."
                        : "Select Role"}
                    </option>

                    {roles.map((role) => (
                      <option
                        key={role.id}
                        value={role.id}
                      >
                        {role.name}
                      </option>
                    ))}
                  </select>

                  {roles.length === 0 && !rolesLoading && (
                    <small className="text-danger">
                      No roles available.
                    </small>
                  )}
                </div>

                {/* ==================================================
                    USERNAME
                ================================================== */}

                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold">
                    Username
                  </label>

                  <input
                    type="text"
                    name="username"
                    className="form-control"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="Enter username"
                    required
                  />
                </div>

                {/* ==================================================
                    LOGIN ID / PHONE NUMBER
                ================================================== */}

                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold">
                    Mobile 
                  </label>

                  <input
                    type="text"
                    name="phone_number"
                    className="form-control"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="Enter 10-digit phone number"
                    maxLength={10}
                    inputMode="numeric"
                    required
                  />

                  
                </div>

                {/* ==================================================
                    EMAIL
                ================================================== */}

                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold">
                    Email
                  </label>

                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    required
                  />
                </div>

                {/* ==================================================
                    PASSWORD
                ================================================== */}

                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold">
                    Password
                  </label>

                  <div className="position-relative">

                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="form-control"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter password"
                      required
                      style={{
                        paddingRight: "45px",
                      }}
                    />

                    <i
                      className={`bx ${
                        showPassword
                          ? "bx-show"
                          : "bx-hide"
                      } position-absolute`}
                      style={{
                        right: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        fontSize: "20px",
                      }}
                      onClick={() =>
                        setShowPassword(!showPassword)
                      }
                    />

                  </div>
                </div>

                {/* ==================================================
                    CONFIRM PASSWORD
                ================================================== */}

                <div className="col-md-6">
                  <label className="form-label text-dark fw-semibold">
                    Confirm Password
                  </label>

                  <div className="position-relative">

                    <input
                      type={
                        showConfirmPassword
                          ? "text"
                          : "password"
                      }
                      name="password_confirmation"
                      className="form-control"
                      value={formData.password_confirmation}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      required
                      style={{
                        paddingRight: "45px",
                      }}
                    />

                    <i
                      className={`bx ${
                        showConfirmPassword
                          ? "bx-show"
                          : "bx-hide"
                      } position-absolute`}
                      style={{
                        right: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        fontSize: "20px",
                      }}
                      onClick={() =>
                        setShowConfirmPassword(
                          !showConfirmPassword
                        )
                      }
                    />

                  </div>
                </div>

                {/* ==================================================
                    SUBMIT
                ================================================== */}

                <div className="col-12 mt-3">

                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={loading || rolesLoading}
                  >
                    {loading
                      ? "Creating..."
                      : "Create User"}
                  </button>

                </div>

              </div>

            </form>

          </div>

        </div>

      </div>
    </DashboardWrapper>
  );
}